import type { ReactNode, Ref } from 'react';
import './templateToggle.css';

/**
 * The option box every list in the application draws: the masters' `ms-opt`
 * row -- a 20px box that fills with a tick when chosen, then its label.
 *
 * Masters render it inside Dropdown and MultiSelect panels. A checkbox field
 * reuses it as is; a radio group draws it round with a dot when chosen
 * (`ms-opt__box--radio`, 2026-10-14), so the two never look alike. PrimeReact's own `Checkbox` and
 * `RadioButton` have no styling in this application (no theme is loaded), and
 * the masters sheet already hides their native inputs.
 *
 * Buttons carry the semantics: `role="checkbox"` / `role="radio"` with
 * `aria-checked`, so Space and Enter toggle them and assistive technology reads
 * them as what they are. PrimeFlex utilities strip the button chrome.
 */

const OPTION_BUTTON = 'ms-opt bg-transparent border-none p-0 cursor-pointer text-left';

interface TemplateCheckboxProps {
  id: string;
  inputRef: Ref<HTMLButtonElement>;
  /** The field's own label, drawn beside the box. */
  label: string;
  checked: boolean;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
  describedBy?: string;
  /** The required marker, beside the label. */
  marker: ReactNode;
  onBlur: () => void;
  onChange: (checked: boolean) => void;
}

/** Checkbox: "☐ Urgent", the label beside its box. */
export function TemplateCheckbox({
  id,
  inputRef,
  label,
  checked,
  disabled,
  required,
  invalid,
  describedBy,
  marker,
  onBlur,
  onChange,
}: TemplateCheckboxProps) {
  return (
    <button
      ref={inputRef}
      id={id}
      type="button"
      role="checkbox"
      className={OPTION_BUTTON}
      aria-checked={checked}
      aria-label={label}
      aria-required={required}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      disabled={disabled}
      onBlur={onBlur}
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className={`ms-opt__box${checked ? ' is-on' : ''}`} aria-hidden="true">
        {checked ? <i className="pi pi-check" /> : null}
      </span>
      <span className="ms-field__label m-0">
        {label}
        {marker}
      </span>
    </button>
  );
}

interface TemplateRadioGroupProps {
  id: string;
  inputRef: Ref<HTMLButtonElement>;
  labelId: string;
  options: readonly string[];
  value: string;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
  describedBy?: string;
  onBlur: () => void;
  onChange: (value: string) => void;
}

/** Radio: one value, every option visible, in the template's order. */
export function TemplateRadioGroup({
  id,
  inputRef,
  labelId,
  options,
  value,
  disabled,
  required,
  invalid,
  describedBy,
  onBlur,
  onChange,
}: TemplateRadioGroupProps) {
  return (
    <div
      id={id}
      role="radiogroup"
      className="flex flex-wrap gap-3"
      aria-labelledby={labelId}
      aria-required={required}
      aria-invalid={invalid}
      aria-describedby={describedBy}
    >
      {options.map((option, index) => (
        <button
          key={option}
          ref={index === 0 ? inputRef : undefined}
          type="button"
          role="radio"
          className={OPTION_BUTTON}
          aria-checked={value === option}
          disabled={disabled}
          onBlur={onBlur}
          onClick={() => {
            onChange(option);
          }}
        >
          {/* Round, with a dot when chosen -- a radio as the standard draws one, not the checkbox's square (2026-10-14). */}
          <span className={`ms-opt__box ms-opt__box--radio${value === option ? ' is-on' : ''}`} aria-hidden="true">
            {value === option ? <span className="ms-opt__dot" /> : null}
          </span>
          <span className="ms-opt__label">{option}</span>
        </button>
      ))}
    </div>
  );
}

interface TemplateToggleProps {
  id: string;
  inputRef: Ref<HTMLButtonElement>;
  /** The field's own label, which names the switch (drawn above it, as every other field's). */
  label: string;
  on: boolean;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
  describedBy?: string;
  onBlur: () => void;
  onChange: (on: boolean) => void;
}

/**
 * Toggle (design21, 2026-10-22): an On / Off switch holding a boolean -- `role="switch"`, so Space and Enter flip it.
 * Off is an answer, not an empty field.
 */
export function TemplateToggle({ id, inputRef, label, on, disabled, required, invalid, describedBy, onBlur, onChange }: TemplateToggleProps) {
  return (
    <button
      ref={inputRef}
      id={id}
      type="button"
      role="switch"
      className={`template-toggle${on ? ' is-on' : ''}${invalid ? ' is-invalid' : ''}`}
      aria-checked={on}
      aria-label={label}
      aria-required={required}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      disabled={disabled}
      onBlur={onBlur}
      onClick={() => {
        onChange(!on);
      }}
    >
      <span className="template-toggle__track" aria-hidden="true">
        <span className="template-toggle__knob" />
      </span>
      <span className="template-toggle__text">{on ? 'On' : 'Off'}</span>
    </button>
  );
}
